import React, { useRef, useCallback, useState } from 'react';
import { Plus, X, Loader2, VideoIcon, Play, Pause, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// VideoThumbnail — manages its own playback state and first-frame render
// ---------------------------------------------------------------------------

interface VideoThumbnailProps {
  src: string;
  onEdit?: () => void;
  onDelete: () => void;
}

const VideoThumbnail: React.FC<VideoThumbnailProps> = ({ src, onEdit, onDelete }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Seek slightly past 0 so the browser renders an actual frame instead of black
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0.1;
    }
  };

  const handleTogglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    if (isPlaying) {
      v.pause();
    } else {
      v.play();
    }
  };

  return (
    <div className="relative flex-shrink-0 w-28 h-28 rounded-xl overflow-hidden border-2 border-border snap-start bg-black">
      <video
        ref={videoRef}
        src={src}
        className="w-full h-full object-cover"
        muted
        playsInline
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
      />

      {/* Play / Pause toggle */}
      <button
        type="button"
        onClick={handleTogglePlay}
        className="absolute inset-0 flex items-center justify-center"
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        <div className="bg-black/40 rounded-full p-1.5">
          {isPlaying
            ? <Pause className="h-4 w-4 text-white fill-white" />
            : <Play  className="h-4 w-4 text-white fill-white" />
          }
        </div>
      </button>

      {/* Edit button */}
      {onEdit && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          className="absolute top-1.5 left-1.5 bg-black/60 hover:bg-black/80 text-white rounded-full p-1 transition-colors"
          aria-label="Edit video"
        >
          <Pencil className="h-3 w-3" />
        </button>
      )}

      {/* Delete button */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="absolute top-1.5 right-1.5 bg-black/60 hover:bg-red-600 text-white rounded-full p-1 transition-colors"
        aria-label="Remove video"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
};

const MAX_VIDEO_SIZE = 500 * 1024 * 1024; // 500 MB — duration is the real gate (30s max enforced upstream)

export interface VideoEntry {
  id: string;          // stable ID — never changes after creation
  localUrl: string;
  uploadedUrl: string;
  isUploading: boolean;
  status?: 'compressing' | 'uploading'; // shown on the loading thumbnail
}

interface MultiVideoUploaderProps {
  videos: VideoEntry[];
  onAdd: (file: File) => void;
  onDelete: (id: string) => void;
  onEdit?: (id: string) => void;
  maxVideos?: number;
}

export const MultiVideoUploader: React.FC<MultiVideoUploaderProps> = ({
  videos,
  onAdd,
  onDelete,
  onEdit,
  maxVideos = 3,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canAddMore = videos.length < maxVideos;

  const handleWebPicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      const remaining = maxVideos - videos.length;
      const toAdd = files.slice(0, remaining);

      for (const file of toAdd) {
        if (file.size > MAX_VIDEO_SIZE) {
          toast.error(`"${file.name}" is too large. Max file size is 500 MB.`);
          continue;
        }
        onAdd(file);
      }

      e.target.value = '';
    },
    [onAdd, videos.length, maxVideos]
  );

  return (
    <div className="space-y-3">
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        multiple
        className="hidden"
        onChange={handleFileChange}
      />

      <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory">
        {videos.map((video) => (
          video.isUploading ? (
            /* Compressing / uploading state */
            <div
              key={video.id}
              className="relative flex-shrink-0 w-28 h-28 rounded-xl overflow-hidden border-2 border-border snap-start bg-black flex flex-col items-center justify-center gap-1"
            >
              <Loader2 className="h-5 w-5 text-white animate-spin" />
              <span className="text-[10px] text-white/60">
                {video.status === 'compressing' ? 'Compressing…' : 'Uploading…'}
              </span>
            </div>
          ) : (
            <VideoThumbnail
              key={video.id}
              src={video.localUrl}
              onEdit={onEdit ? () => onEdit(video.id) : undefined}
              onDelete={() => onDelete(video.id)}
            />
          )
        ))}

        {canAddMore && (
          <button
            type="button"
            onClick={handleWebPicker}
            className={cn(
              'flex-shrink-0 w-28 h-28 rounded-xl border-2 border-dashed border-border',
              'flex flex-col items-center justify-center gap-1.5',
              'text-muted-foreground hover:border-[#cfef00] hover:text-[#cfef00]',
              'transition-colors snap-start'
            )}
          >
            {videos.length === 0 ? (
              <>
                <VideoIcon className="h-7 w-7" />
                <span className="text-xs font-medium">Add Video</span>
              </>
            ) : (
              <>
                <Plus className="h-6 w-6" />
                <span className="text-xs">{videos.length}/{maxVideos}</span>
              </>
            )}
          </button>
        )}
      </div>

      {videos.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {videos.length}/{maxVideos} added &middot; max 30s per video
        </p>
      )}
    </div>
  );
};
